import { __awaiter } from "tslib";
import { getOauthClientId } from '@local/app-config';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { LOGIN_SUCCESS_ROUTE } from '../../../constants';
import { AUTH_CODE_KEY, getCombinedToken as getAccessToken, setCombinedToken as setAccessToken, } from '../../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import { getPkceVerifier } from '../../../store/sessionStorageHelpers/pkceFlowHelpers';
import { IdentityApiClient } from '../IdentityApiClient';
export function accessTokenBody(code, includeClientSecret, forceOauthPrefix) {
    const clientId = getOauthClientId(forceOauthPrefix);
    const redirectUrl = `${window.location.origin}${LOGIN_SUCCESS_ROUTE}` || '';
    const urlEncodedBody = new URLSearchParams();
    urlEncodedBody.append('code', code);
    urlEncodedBody.append('grant_type', 'authorization_code');
    urlEncodedBody.append('redirect_uri', redirectUrl);
    urlEncodedBody.append('client_id', clientId);
    if (includeClientSecret) {
        urlEncodedBody.append('client_secret', clientId);
    }
    const verifier = getPkceVerifier();
    urlEncodedBody.append('code_verifier', verifier);
    return urlEncodedBody;
}
export function refreshTokenBody(refreshToken, forceOauthPrefix, includeClientSecret) {
    const clientId = getOauthClientId(forceOauthPrefix);
    const urlEncodedBody = new URLSearchParams();
    urlEncodedBody.append('grant_type', 'refresh_token');
    urlEncodedBody.append('refresh_token', refreshToken);
    urlEncodedBody.append('client_id', clientId);
    if (includeClientSecret) {
        urlEncodedBody.append('client_secret', clientId);
    }
    return urlEncodedBody;
}
const tokenMutex = new Mutex();
export const AccessTokenApi = IdentityApiClient.injectEndpoints({
    endpoints: (build) => ({
        retrieveAccessToken: build.query({
            queryFn(_a, _api_1, _extraOptions_1, baseQuery_1) {
                return __awaiter(this, arguments, void 0, function* ({ authCode, includeClientSecret, forceOauthPrefix }, _api, _extraOptions, baseQuery) {
                    const accessTokenResponse = yield baseQuery({
                        url: 'oauth2/access',
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: accessTokenBody(authCode, includeClientSecret, forceOauthPrefix),
                    });
                    if (accessTokenResponse.error) {
                        return { error: accessTokenResponse.error };
                    }
                    setAccessToken(accessTokenResponse.data);
                    sessionStorage.setItem(AUTH_CODE_KEY, authCode);
                    return { data: accessTokenResponse.data };
                });
            },
        }),
        refreshAccessToken: build.query({
            queryFn(_a, _api_1, _extraOptions_1, baseQuery_1) {
                return __awaiter(this, arguments, void 0, function* ({ forceOauthPrefix, includeClientSecret, refreshToken }, _api, _extraOptions, baseQuery) {
                    const accessTokenResponse = yield baseQuery({
                        url: 'oauth2/access',
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: refreshTokenBody(refreshToken, forceOauthPrefix, includeClientSecret),
                    });
                    if (accessTokenResponse.error) {
                        return { error: accessTokenResponse.error };
                    }
                    setAccessToken(accessTokenResponse.data);
                    return { data: accessTokenResponse.data };
                });
            },
        }),
    }),
});
export function createAccessTokenBaseQuery(urlConfig, transformedFilterParams) {
    return (args, WebApi, extraOptions) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        let currentArgs;
        if (transformedFilterParams) {
            currentArgs = transformedFilterParams(args);
        }
        else {
            currentArgs = args;
        }
        const urlConfiguration = urlConfig();
        if (urlConfiguration === null) {
            return { error: { status: 500, data: 'Not enough information to generate url' } };
        }
        const { url: baseUrl, forceOauthPrefix, includeClientSecret } = urlConfiguration;
        const rawBaseQuery = fetchBaseQuery({
            baseUrl,
            prepareHeaders: (headers) => {
                const token = getAccessToken();
                if (token) {
                    headers.set('authorization', `Bearer ${token === null || token === void 0 ? void 0 : token.access_token}`);
                }
                return headers;
            },
        });
        yield tokenMutex.waitForUnlock();
        let result = yield rawBaseQuery(currentArgs, WebApi, extraOptions);
        const originalToken = getAccessToken();
        if (result.error && result.error.status === 401 && originalToken) {
            const refreshToken = (_a = originalToken === null || originalToken === void 0 ? void 0 : originalToken.refresh_token) !== null && _a !== void 0 ? _a : false;
            if (!refreshToken) {
                return result;
            }
            if (!tokenMutex.isLocked()) {
                const refreshResult = yield WebApi.dispatch(AccessTokenApi.endpoints.refreshAccessToken.initiate({
                    forceOauthPrefix,
                    includeClientSecret,
                    refreshToken,
                }, { subscribe: true, forceRefetch: true }));
                const newToken = getAccessToken();
                if ((refreshResult === null || refreshResult === void 0 ? void 0 : refreshResult.isSuccess) &&
                    originalToken.access_token !== (newToken === null || newToken === void 0 ? void 0 : newToken.access_token)) {
                    result = yield rawBaseQuery(currentArgs, WebApi, extraOptions);
                }
            }
            else {
                yield tokenMutex.waitForUnlock();
                result = yield rawBaseQuery(currentArgs, WebApi, extraOptions);
            }
        }
        return result;
    });
}
export const { useRetrieveAccessTokenQuery, useLazyRefreshAccessTokenQuery } = AccessTokenApi;
//# sourceMappingURL=AccessTokenApi.js.map